import { Star, MapPin, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../lib/api';

export default function CancelAppointmentPage() {
  const [selectedReason, setSelectedReason] = useState('');
  const [otherText, setOtherText] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const maxChars = 300;
  const styles = {
    backArrow: {
      cursor: 'pointer',
      fontSize: '20px',
      color: 'var(--primary)',
    },
  };

  // Get appointment data passed from previous page
  const location = useLocation();
  const { consultation } = location.state || {}; // consultation to cancel
  const [doctor, setDoctor] = useState(consultation?.doctor || consultation?.doctor_ || null);

  // Fetch doctor info
  useEffect(() => {
    async function fetchDoctor() {
      if (!consultation?.doctor) return;
      try {
        const doctorId =
          typeof consultation.doctor === 'string'
            ? consultation.doctor
            : consultation.doctor?._id;

        if (!doctorId) return;
        const res = await api.get(`/api/doctor/${doctorId}`);
        setDoctor(res.data);
      } catch (err) {
        console.error('Failed to fetch doctor:', err);
      }
    }
    fetchDoctor();
  }, [consultation]);

  const handleCancel = async () => {
    if (!selectedReason) return;
    setLoading(true);
    const reasonText = selectedReason === 'Other' ? otherText : selectedReason;

    try {
      await api.put(`/api/consultations/${consultation._id}/cancel`, { reason: reasonText });
      alert('Appointment cancelled successfully!');
      navigate('/appoint', { state: { notice: 'Appointment canceled successfully.' } });
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
      alert('Failed to cancel appointment.');
    } finally {
      setLoading(false);
    }
  };

  if (!consultation) {
    return (
      <div className="container py-4 px-3" style={{ maxWidth: 480 }}>
        <div className="alert alert-warning" role="alert">
          No appointment selected. Please choose an appointment from My Booking.
        </div>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/appoint')}>
          Back to My Booking
        </button>
      </div>
    );
  }

  if (!doctor) return <p>Loading...</p>;

  return (
    <div className="container py-4 px-3" style={{ maxWidth: 480 }}>
      {/* Header */}
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn p-0 me-3"
          onClick={() => navigate(-1)}
          style={{ border: 'none', background: 'none' }}
        >
          <i className="fi fi-br-angle-left" style={styles.backArrow}></i>
        </button>
        <h5 className="fw-semibold mb-0" style={{ color: 'var(--primary)' }}>Cancel Appointment</h5>
      </div>

      {/* Warning */}
      <div
        className="alert d-flex align-items-center gap-2 shadow-sm rounded-4"
        style={{ fontSize: 13, background: 'rgba(127, 0, 255, 0.06)', borderColor: 'rgba(127, 0, 255, 0.24)', color: 'var(--on-bg)' }}
      >
        <AlertCircle size={16} color="var(--primary)" />
        Too many cancellations may affect your account.
      </div>

      {/* Doctor Card */}
      <div className="card shadow-sm rounded-4 p-3 mb-4" style={{ background: 'linear-gradient(135deg, rgba(127, 0, 255, 0.08), #ffffff)', border: '1px solid rgba(127, 0, 255, 0.14)' }}>
        <div className="d-flex align-items-center">
          <img
            src={doctor.avatar || doctor.photo || 'https://randomuser.me/api/portraits/men/45.jpg'}
            alt={doctor.name}
            className="rounded-circle me-3"
            style={{ width: 70, height: 70, objectFit: 'cover', border: '3px solid rgba(127, 0, 255, 0.28)' }}
          />
          <div>
            <h6 className="fw-semibold mb-0">{doctor.name}</h6>
            <p className="text-muted mb-1" style={{ fontSize: 13 }}>
              {doctor.specialty || doctor.specialisation} • <Star size={13} className="text-warning me-1" />
              {doctor.rating || doctor.averageRating}★ ({doctor.reviews || 0})
            </p>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              <MapPin size={13} className="me-1" />
              {doctor.distance || 'Unknown'}
            </p>
            <p className="text-muted mt-1 mb-0" style={{ fontSize: 13 }}>
              📅 {new Date(consultation.appointmentTime).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Cancellation Reason */}
      <h6 className="fw-semibold mb-3">Select Cancellation Reason</h6>
      <div className="mb-4 d-flex flex-column gap-2">
        {[
          'I found a better provider',
          "I'm concerned about the insurance",
          "The provider doesn't accept insurance",
          'Provider asked to cancel',
          'There was an issue on my end',
          'Other',
        ].map((reason, idx) => (
          <button
            key={idx}
            type="button"
            className={`btn text-start rounded-4 shadow-sm ${selectedReason === reason ? '' : 'btn-outline-secondary'}`}
            style={{
              fontSize: 14,
              padding: '10px 14px',
              borderColor: selectedReason === reason ? 'var(--primary)' : undefined,
              background: selectedReason === reason ? 'var(--primary)' : undefined,
              color: selectedReason === reason ? '#fff' : undefined,
            }}
            onClick={() => setSelectedReason(reason)}
          >
            {reason}
          </button>
        ))}
      </div>

      {/* Other Reason Input */}
      {selectedReason === 'Other' && (
        <div className="mb-4">
          <textarea
            className="form-control shadow-sm rounded-4"
            rows={3}
            maxLength={maxChars}
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            placeholder="Please specify your reason..."
            style={{ fontSize: 14, padding: '10px 14px', borderColor: 'rgba(127, 0, 255, 0.22)' }}
          />
          <div className="text-end text-muted mt-1" style={{ fontSize: 12 }}>
            {otherText.length}/{maxChars}
          </div>
        </div>
      )}

      {/* Continue Button */}
      <button
        className="btn w-100 py-3 fw-semibold rounded-4 shadow-sm"
        style={{
          fontSize: 16,
          background: 'var(--primary)',
          borderColor: 'var(--primary)',
          color: '#fff',
          opacity: (!selectedReason || (selectedReason === 'Other' && !otherText.trim()) || loading) ? 0.7 : 1,
        }}
        disabled={!selectedReason || (selectedReason === 'Other' && !otherText.trim()) || loading}
        onClick={handleCancel}
      >
        {loading ? 'Cancelling...' : 'Cancel Appointment'}
      </button>
    </div>
  );
}
